import { useMemo } from 'react';
import { useExportProjectHighlightsModal } from '@/components/GroupProject/ExportProjectHighlightsModal';
import { useExportProjectInsightsModal } from '@/components/Project.Insights';
import MoveObjectModal from '@/components/WorkspaceObject/MoveObjectModal';
import { useUploadQuantAnalysisFileModal } from '@/containers/QuantAnalysis/hooks';
import { ProjectAction, WorkspaceObjectType } from '@/enums';
import { useToggle } from '@trinity/chat-components/hooks/useToggle';
import { useAppSelector } from '@/store';
import type { ProjectBase, WorkspaceObject } from '@/types';
import type { ProjectCapabilities } from '@/types/workspace.access';
import MoveObjectContainer from '../WorkspaceObject/MoveObjectContainer';
import type { ProjectActionsContextValue } from './Context';
import {
  ProjectActionsContext,
  ProjectActionsValidityContext,
} from './Context';
import { useProjectActionsValidity } from './hooks';
import * as utils from './utils';

type ProjectMinimumProps =
  Pick<ProjectBase,
    | 'id'
    | 'name'
    | 'parentId'
    | 'projectStatusId'
    | 'projectTypeId'>;

type ValidityProps = {
  children: React.ReactNode;
  capabilities: ProjectCapabilities;
  // parent: Pick<ProjectParent, 'frequency'>;
  project: ProjectMinimumProps;
};

type Props = {
  children: React.ReactNode;
  actionOnParent?: boolean;
  capabilities: ProjectCapabilities;
  // parent: Pick<ProjectParent, 'frequency'>;
  project: ProjectMinimumProps;
  object: WorkspaceObject<string>;
};

export const ProjectActionsValidityContainer = ({ capabilities, children, project }: ValidityProps) => {
  // const canSchedule = useHasSchedulingPermission();
  const worskpacesEnabled = useAppSelector((state) => state.features.workspaces);
  // const isInternalUser = useIsInternalUser();

  const value: Record<ProjectAction, boolean> = useMemo(() => ({
    // [ProjectAction.Activate]: utils.canActivate(project) && capabilities.canManage && isInternalUser,
    // [ProjectAction.Archive]: utils.canArchive(project) && capabilities.canManage && isInternalUser,
    // [ProjectAction.ChangeOwner]: capabilities.canChangeOwner,
    // [ProjectAction.Clone]: utils.canClone(parent) && capabilities.canManage && isInternalUser,
    // [ProjectAction.CreateCall]: utils.isCallLike(project) && canSchedule && isInternalUser,
    // [ProjectAction.DownloadCallReport]: utils.canDownload(project) && utils.isCallLike(project),// && isInternalUser,
    [ProjectAction.DownloadHighlightsReport]: utils.isCallLike(project),
    [ProjectAction.DownloadInsightsReport]: utils.isCallLike(project),
    // [ProjectAction.DownloadTagSummariesReport]: utils.isCallLike(project), //&& isInternalUser,
    // [ProjectAction.DownloadResponseGrid]: utils.isCallLike(project), //&& isInternalUser,
    // [ProjectAction.DownloadReport]: utils.canDownload(project),

    // [ProjectAction.DownloadSurveyReport]: utils.isSurveyLike(project),

    // [ProjectAction.EditSurvey]: utils.canEditSurvey(project) && capabilities.canManage && isInternalUser,
    // [ProjectAction.ManageAccess]: utils.canManage(project) && capabilities.canManageAccess,
    // [ProjectAction.MonitorSurvey]: utils.canMonitorSurvey(project) && isInternalUser,
    [ProjectAction.Move]: worskpacesEnabled && capabilities.canMove,
    // [ProjectAction.Share]: utils.canShare(project) && isInternalUser,
    // [ProjectAction.Preview]: false, //utils.canPreview(project) && isInternalUser,
    // [ProjectAction.ManageDisplayrDashboard]: capabilities.canManage && utils.isSurveyLike(project) && isInternalUser,
    // [ProjectAction.ManageSelectDashboard]: capabilities.canManage && utils.isSurveyLike(project) && isInternalUser,
    // [ProjectAction.ManageSurveySettings]: utils.canManage(project) && utils.isSurveyLike(project) && capabilities.canManage && isInternalUser,
    // [ProjectAction.UploadQuantAnalysisFileFromExcel]: isInternalUser && project.projectType === ProjectType.Survey,
    // [ProjectAction.UploadQuantAnalysisFileFromJson]: isInternalUser && project.projectType === ProjectType.Survey,
    [ProjectAction.UploadQuantAnalysisFile]: true, // Always show for testing
  }), [capabilities.canMove, project, worskpacesEnabled]);

  return (
    <ProjectActionsValidityContext.Provider value={value}>
      {children}
    </ProjectActionsValidityContext.Provider>
  );
};

export const ProjectActionsContainer = ({ actionOnParent: _actionOnParent, project, children, object }: Omit<Props, 'capabilities'>) => {

  // const respondent = useContext(RespondentSetupContext);

  const validity = useProjectActionsValidity();

  // const history = useHistory();

  // const [toggleActivatePrompt, ActivateProjectPrompt] = useActivateGroupProjectPrompt({ project });
  // const [toggleArchivePrompt, ArchiveProjectPrompt] = useArchiveGroupProjectPrompt({ project });
  // const [toggleClonePrompt, CloneProjectPrompt] = useCloneProjectPrompt({ project });
  // const [toggleShareModal, ShareWithExpertModal] = useShareWithExpertModal({ project: project });
  // const [toggleOwnershipModal, OwnershipModal] = useObjectOwnershipModal();
  // const [toggleReportModal, ReportModal] = useProjectReportModal();
  // const [toggleCallReportModal, CallReportModal] = useExportCallModal();
  // const [toggleSurveyReportModal, SurveyReportModal] = useExportSurveyModal();
  const [toggleExportHighlightsModal, ExportHighlightsModal] = useExportProjectHighlightsModal();
  // const [toggleTagSummariesModal, TagSummariesModal] = useProjectConferenceTagSummariesModal();
  // const [toggleResponseGridModal, ResponseGridModal] = useProjectResponseGridModal();
  const [toggleInsightsModal, InsightsModal] = useExportProjectInsightsModal();
  const [moveObjectModalOpen, toggleMoveObjectModal] = useToggle();
  // const [toggleDisplayrDashboardModal, DisplayrDashboardModal] = useDisplayrDashboardModal();
  // const [selectDashboardModalOpen, toggleSelectDashboardModal] = useToggle();
  // const [sigmaDashboardModalOpen, toggleSigmaDashboardModal] = useToggle();
  // const [tableauDashboardModalOpen, toggleTableauDashboardModal] = useToggle();

  // Add upload quant analysis file modal
  const [toggleUploadQuantFile, UploadQuantAnalysisModal] = useUploadQuantAnalysisFileModal({
    projectId: project.id,
  });

  // const displayrDashboardId = useContext(ProjectDisplayrDashboardIdContext);

  // const toggleCallScheduling = useCallback(() => {
  //   respondent.setContext({ projectId: project.id });
  // }, [
  //   respondent,
  //   project,
  // ]);

  // const editSurvey = useCallback(() => {
  //   history.push(getLocationFor.project.surveys({ slug: String(project.id) }));
  // }, [
  //   history,
  //   project.id,
  // ]);

  // const monitorSurvey = useCallback(() => {
  //   history.push(getLocationFor.project.surveyMonitoring({ slug: String(project.id) }));
  // }, [
  //   history,
  //   project.id,
  // ]);

  // const manageSurveySettings = useCallback(() => {
  //   history.push(getLocationFor.project.surveySettings({ projectId: project.id }));
  // }, [history, project.id]);

  // const handleManageDashboard = useCallback(() => {
  //   toggleSelectDashboardModal();
  // }, [toggleSelectDashboardModal]);

  // const handleDashboardContinue = useCallback((selected: string) => {
  //   toggleSelectDashboardModal(); // Close the select modal
  //   if (selected === 'displayr') {
  //     toggleDisplayrDashboardModal(); // Open Displayr modal
  //   } else if (selected === 'sigma') {
  //     toggleSigmaDashboardModal(); // Open Sigma modal
  //   } else if (selected === 'tableau') {
  //     toggleTableauDashboardModal(); // Open Tableau modal
  //   }
  // }, [toggleSelectDashboardModal, toggleDisplayrDashboardModal, toggleSigmaDashboardModal, toggleTableauDashboardModal]);

  // const [toggleQuantAnalysisExcelFileUpload, UploadQuantAnalysisExcelFileModal] = useUploadQuantAnalysisExcelModal();
  // const [toggleQuantAnalysisJsonFileUpload, UploadQuantAnalysisJsonFileModal] = useUploadQuantAnalysisJsonModal();

  // Upload quant analysis file action now uses real modal

  const value: ProjectActionsContextValue = useMemo(() => ({
    // [ProjectAction.Activate]: toggleActivatePrompt,
    // [ProjectAction.Archive]: toggleArchivePrompt,
    // [ProjectAction.ChangeOwner]: toggleOwnershipModal,
    // [ProjectAction.Clone]: toggleClonePrompt,
    // [ProjectAction.CreateCall]: toggleCallScheduling,
    // [ProjectAction.EditSurvey]: editSurvey,
    // [ProjectAction.DownloadCallReport]: toggleCallReportModal,
    [ProjectAction.DownloadHighlightsReport]: toggleExportHighlightsModal,
    [ProjectAction.DownloadInsightsReport]: toggleInsightsModal,

    // [ProjectAction.DownloadSurveyReport]: toggleSurveyReportModal,

    // [ProjectAction.DownloadReport]: toggleReportModal,
    // [ProjectAction.DownloadTagSummariesReport]: toggleTagSummariesModal,
    // [ProjectAction.DownloadResponseGrid]: toggleResponseGridModal,
    [ProjectAction.Move]: toggleMoveObjectModal,
    // [ProjectAction.Share]: toggleShareModal,
    // [ProjectAction.MonitorSurvey]: monitorSurvey,
    // [ProjectAction.ManageDisplayrDashboard]: toggleDisplayrDashboardModal,
    // [ProjectAction.ManageSelectDashboard]: handleManageDashboard,
    // [ProjectAction.ManageAccess]: null,
    // [ProjectAction.Preview]: null,
    // [ProjectAction.ManageSurveySettings]: manageSurveySettings,
    // [ProjectAction.UploadQuantAnalysisFileFromExcel]: toggleQuantAnalysisExcelFileUpload,
    // [ProjectAction.UploadQuantAnalysisFileFromJson]: toggleQuantAnalysisJsonFileUpload,
    [ProjectAction.UploadQuantAnalysisFile]: toggleUploadQuantFile,
    projectId: project.id,
  }), [
    // editSurvey,
    // toggleActivatePrompt,
    // toggleArchivePrompt,
    // toggleCallReportModal,
    // toggleCallScheduling,
    // toggleClonePrompt,
    toggleExportHighlightsModal,
    toggleMoveObjectModal,
    // toggleOwnershipModal,
    // toggleTagSummariesModal,
    // toggleResponseGridModal,
    // toggleReportModal,
    // toggleShareModal,
    // toggleSurveyReportModal,
    toggleInsightsModal,
    // toggleDisplayrDashboardModal,
    // handleManageDashboard,
    // toggleQuantAnalysisExcelFileUpload,
    // toggleQuantAnalysisJsonFileUpload,
    toggleUploadQuantFile,
    project.id,
    // monitorSurvey,
    // manageSurveySettings,
  ]);

  return (
    <>
      <ProjectActionsContext.Provider value={value}>
        {children}
      </ProjectActionsContext.Provider>
      {/* <ReportModal
        isParentReport={actionOnParent}
        project={project} />
      <CallReportModal
        isParentReport={actionOnParent}
        project={project} /> */}
      {/* <SurveyReportModal
        isParentReport={actionOnParent}
        surveyId={null} //TODO: fill this in with either a selected survey or the known primary survey
        project={project} /> */}
      {/* {validity[ProjectAction.UploadQuantAnalysisFileFromExcel] && (
        <UploadQuantAnalysisExcelFileModal projectId={project.id} />
      )}
      {validity[ProjectAction.UploadQuantAnalysisFileFromJson] && (
        <UploadQuantAnalysisJsonFileModal projectId={project.id} />
      )} */}
      {validity[ProjectAction.DownloadHighlightsReport] && <ExportHighlightsModal projectId={project.id} />}
      {/* {validity[ProjectAction.DownloadResponseGrid] && <ResponseGridModal projectId={project.id} />}
      {validity[ProjectAction.DownloadTagSummariesReport] && <TagSummariesModal projectId={project.id} />} */}
      {/* {validity[ProjectAction.Archive] && <ArchiveProjectPrompt />}
      {validity[ProjectAction.Activate] && <ActivateProjectPrompt />}
      {validity[ProjectAction.Clone] && <CloneProjectPrompt />}
      {validity[ProjectAction.Share] && <ShareWithExpertModal />} */}
      {validity[ProjectAction.DownloadInsightsReport] && <InsightsModal projectId={project.id} />}
      {/* Upload Quant Analysis File Modal */}
      {validity[ProjectAction.UploadQuantAnalysisFile] && UploadQuantAnalysisModal}
      {/* {
        validity[ProjectAction.ManageDisplayrDashboard] && (
          <DisplayrDashboardModal
            initialDashboardId={displayrDashboardId}
            projectId={project.id} />
        )
      }
      {
        validity[ProjectAction.ChangeOwner] &&
        <ObjectOwnershipContainer object={object} workspaceId={object.workspaceId}>
          <OwnershipModal />
        </ObjectOwnershipContainer>
      } */}
      {
        validity[ProjectAction.Move] &&
        <MoveObjectContainer
          workspaceId={object.workspaceId}
          enabled={moveObjectModalOpen}
          objectId={object.id}
          objectType={WorkspaceObjectType.ProjectParent}>
          <MoveObjectModal
            open={moveObjectModalOpen}
            onClose={toggleMoveObjectModal} />
        </MoveObjectContainer>
      }
      {/* <SelectDashboardModal
        open={selectDashboardModalOpen}
        onClose={toggleSelectDashboardModal}
        onContinue={handleDashboardContinue} />
      <SigmaDashboardModal
        open={sigmaDashboardModalOpen}
        onClose={toggleSigmaDashboardModal}
        onContinue={() => { }}
        projectId={project.id} />

      <TableauDashboardModal
        open={tableauDashboardModalOpen}
        onClose={toggleTableauDashboardModal}
        onContinue={toggleTableauDashboardModal}
        projectId={project.id} /> */}

    </>
  );
};

export const ProjectActionsState = ({ children, ...props }: Props) => {
  return (
    <ProjectActionsValidityContainer {...props}>
      {/* <CallScheduling.Container> */}
      <ProjectActionsContainer {...props}>
        {children}
      </ProjectActionsContainer>
      {/* </CallScheduling.Container> */}
    </ProjectActionsValidityContainer>
  );
};

export default ProjectActionsContainer;